interface NativeDeviceIdentity {
  getOrCreateDeviceId(options: { candidate: string | null }): Promise<{ deviceId: string }>
}

function nativeDeviceIdentity(): NativeDeviceIdentity | undefined {
  return (window as any).Capacitor?.Plugins?.AgentOverlay as NativeDeviceIdentity | undefined
}

/**
 * Device identification utilities.
 *
 * Generates a persistent device ID per platform (web, capacitor, desktop).
 * In the desktop app the ID is stored in desktop-settings.json (survives reinstalls).
 * On other platforms it falls back to localStorage.
 */

export function detectPlatform(): 'desktop' | 'capacitor' | 'web' {
  if (typeof window !== 'undefined' && (window as any).jaitDesktop) return 'desktop'
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.() === true) return 'capacitor'
  return 'web'
}

// Module-level cache so the sync getter always returns immediately after init.
let _cachedDeviceId: string | null = null

function makeDeviceId(platform: string): string {
  return `${platform}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function readStoredDeviceId(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

function persistDeviceId(storageKey: string, deviceId: string): void {
  try {
    localStorage.setItem(storageKey, deviceId)
  } catch {
    // Storage can be unavailable in sandboxed browsers or privacy-constrained contexts.
  }
}

/**
 * Initialise the device ID asynchronously.
 *
 * In the desktop app this reads from persistent desktop-settings.json (via IPC),
 * migrating any existing localStorage value on first run.
 * Must be called once at app startup before relying on `generateDeviceId()`.
 */
export async function initDeviceId(): Promise<string> {
  if (_cachedDeviceId) return _cachedDeviceId

  const platform = detectPlatform()
  const storageKey = `jait-device-id-${platform}`
  const settingsKey = 'deviceId'

  if (platform === 'desktop' && (window as any).jaitDesktop?.getSetting) {
    const desktop = (window as any).jaitDesktop
    // The main process resolves the persistent ID synchronously and exposes it
    // via the preload bridge. Prefer it so init never disagrees with the sync
    // generateDeviceId() getter (which already used the same value).
    const bridgeId = desktop.deviceId as string | undefined
    if (bridgeId) {
      _cachedDeviceId = bridgeId
      persistDeviceId(storageKey, bridgeId)
      // Ensure the persistent settings file agrees (idempotent).
      try { await desktop.setSetting(settingsKey, bridgeId) } catch { /* ignore */ }
      return bridgeId
    }
    // Try persistent desktop settings first
    const persisted = await desktop.getSetting(settingsKey, null) as string | null
    if (persisted) {
      _cachedDeviceId = persisted
      // Keep localStorage in sync for immediate reads
      persistDeviceId(storageKey, persisted)
      return persisted
    }
    // Migrate from localStorage if present (upgrade path)
    const fromStorage = readStoredDeviceId(storageKey)
    if (fromStorage) {
      _cachedDeviceId = fromStorage
      await desktop.setSetting(settingsKey, fromStorage)
      return fromStorage
    }
    // Generate new
    const id = makeDeviceId(platform)
    _cachedDeviceId = id
    persistDeviceId(storageKey, id)
    await desktop.setSetting(settingsKey, id)
    return id
  }

  // Android's native preferences survive WebView origin and storage changes.
  // Seed them with the current ID on upgrade so existing grants remain valid.
  if (platform === 'capacitor' && (window as any).Capacitor?.getPlatform?.() === 'android') {
    try {
      const identity = nativeDeviceIdentity()
      if (!identity) throw new Error('Native device identity unavailable')
      const { deviceId } = await identity.getOrCreateDeviceId({ candidate: readStoredDeviceId(storageKey) })
      _cachedDeviceId = deviceId
      persistDeviceId(storageKey, deviceId)
      return deviceId
    } catch {
      // Older app builds do not expose this native method yet.
    }
  }

  // Web and older native builds: localStorage fallback.
  const stored = readStoredDeviceId(storageKey)
  if (stored) {
    _cachedDeviceId = stored
    return stored
  }
  const id = makeDeviceId(platform)
  _cachedDeviceId = id
  persistDeviceId(storageKey, id)
  return id
}

/**
 * Return the device ID synchronously.
 *
 * If `initDeviceId()` has been called, returns the cached value.
 * Otherwise falls back to localStorage (always works for web/capacitor,
 * works for the desktop app after first run since we sync to localStorage).
 */
export function generateDeviceId(): string {
  if (_cachedDeviceId) return _cachedDeviceId

  const platform = detectPlatform()
  const storageKey = `jait-device-id-${platform}`

  // Desktop: the native process resolves the persistent device ID from
  // desktop-settings.json at startup and exposes it synchronously via the
  // preload bridge. Use it first so that code running before initDeviceId()
  // completes (e.g. project creation on first render) stamps the *real*
  // nodeId onto projects instead of a throwaway random one.
  if (platform === 'desktop') {
    const bridgeId = typeof window !== 'undefined' ? (window as any).jaitDesktop?.deviceId as string | undefined : undefined
    if (bridgeId) {
      _cachedDeviceId = bridgeId
      persistDeviceId(storageKey, bridgeId)
      return bridgeId
    }
  }

  const stored = readStoredDeviceId(storageKey)
  if (stored) {
    _cachedDeviceId = stored
    return stored
  }
  // Fallback: generate and store (should only happen if initDeviceId wasn't called)
  const id = makeDeviceId(platform)
  _cachedDeviceId = id
  persistDeviceId(storageKey, id)
  return id
}
